import React from 'react';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@mantine/dates';

import {
  DataWidgetFiltersInput,
  OPERATORS_WITHOUT_VALUE,
} from 'dashboards/types/filter';

import { formatPretty } from 'services/string';

import { Input } from 'components/Input/Input';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';
import { Select } from 'components/Select/Select';

import { DeploymentFilterInput } from './DeploymentFilterInput';
import { GatewayFilterInput } from './GatewayFilterInput';
import { PipelineFilterInput } from './PipelineFilterInput';
import { StreamFilterInput } from './StreamFilterInput';
import { useDataWidgetFiltersInputState } from '../context';

export const Placeholder = <Select isDisabled isLoading />;

type SuggestionInputProps = Omit<DataWidgetFiltersInput, 'and' | 'or'>;

export function SuggestionInput({
  id,
  column,
  operator,
  value,
}: SuggestionInputProps) {
  const { updateFilter } = useDataWidgetFiltersInputState();

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    updateFilter(id, { value: event.target.value.trim() });
  }

  const isDisabled = OPERATORS_WITHOUT_VALUE.includes(operator);

  function renderInput() {
    switch (column) {
      case 'deployment_id':
        return <DeploymentFilterInput id={id} value={value} />;
      case 'gateway_id':
        return <GatewayFilterInput id={id} value={value} />;
      case 'pipeline_id':
        return <PipelineFilterInput id={id} value={value} />;
      case 'source_id':
        return <StreamFilterInput id={id} value={value} />;
      case 'timestamp':
        return (
          <DateTimePicker
            value={
              value ? DateTime.fromSeconds(Number(value)).toJSDate() : null
            }
            onChange={(date) =>
              date &&
              updateFilter(id, {
                value: String(DateTime.fromJSDate(date).toUnixInteger()),
              })
            }
          />
        );
      default:
        return (
          <Input
            defaultValue={value ? String(value) : undefined}
            onChange={handleInputChange}
          />
        );
    }
  }

  return (
    <OptionalTooltip
      showTooltip={isDisabled}
      content={`Value will be ignored when using "${formatPretty(
        operator
      ).toLowerCase()}" operator`}
    >
      {isDisabled ? <Input disabled /> : renderInput()}
    </OptionalTooltip>
  );
}
